import { payerTypes } from "pages/PayerRulesPage/helpers/helper";
import {
  BenefitServiceLine,
  BenefitServiceType,
  BenefitTierPreference,
} from "types/BenefitSelection/BenefitSelection";

/**
 * CLE-2792 — the words this screen uses, in one place.
 *
 * 🚨 THESE ARE CALLED "SERVICE LINES" EVERYWHERE, NEVER "BLOCKS" OR "CARE SETTINGS". A person
 * configuring an account is choosing which of the payer's own service lines their copay is read
 * from, and the payer's own name for it is printed underneath so the choice can be checked against
 * a response rather than against our vocabulary.
 *
 * The ORDER IS FIXED, and nothing on the screen ranks the lines: the choice is about how the
 * facility bills, not about which line the payer happens to describe most often. The "copay
 * reported" counts that used to sit beside each line were removed for saying the latter while
 * reading as the former.
 */
export const SERVICE_LINES: Array<{
  value: BenefitServiceLine;
  label: string;
  /** pVerify's own name(s) for it, shown as the subtitle. */
  pverifyNames: string;
}> = [
  {
    value: "ASC",
    label: "Ambulatory surgery center",
    pverifyNames:
      "Ambulatory Service Center Facility, Hospital - Ambulatory Surgical",
  },
  {
    value: "HOSPITAL_OUTPATIENT",
    label: "Hospital outpatient",
    pverifyNames: "Hospital - Outpatient",
  },
  {
    value: "HOSPITAL_INPATIENT",
    label: "Hospital inpatient",
    pverifyNames: "Hospital - Inpatient",
  },
  {
    value: "SPECIALIST_OFFICE",
    label: "Physician office",
    pverifyNames: "Professional (Physician) Visit - Office",
  },
  {
    value: "SURGICAL",
    label: "Surgical (physician outpatient)",
    pverifyNames: "Surgical",
  },
];

export const serviceLineLabel = (
  value: BenefitServiceType | "XRAY" | null | undefined
): string => {
  if (!value) return "Not set";
  if (value === "MRI_CT") return "MRI/CAT Scan";
  if (value === "PLAN_LEVEL") return "Plan level";
  // CLE-3013 — the donor-only source: MRI/CT co-insurance borrows from it. Never a service line a
  // person picks, so it is not in `SERVICE_LINES`.
  if (value === "XRAY") return "Diagnostic X-Ray";
  // CLE-3013 — the block a Diagnostic Medical (113) estimate reads, like MRI/CAT Scan for MRI/CT.
  if (value === "DIAGNOSTIC_MEDICAL") return "Diagnostic Medical";
  return SERVICE_LINES.find((line) => line.value === value)?.label ?? value;
};

/** The five money fields, in the order the "What this account reads" table lists them. */
export const BENEFIT_LABELS: Record<string, string> = {
  FLAT_COPAY: "Copay",
  COINSURANCE_PERCENT: "Co-insurance",
  PROFESSIONAL_COPAY: "Physician copay",
  REMAINING_DEDUCTIBLE: "Remaining deductible",
  REMAINING_OOP: "Remaining out-of-pocket",
};

/**
 * The five Benefit Priority values, with the words the account card uses for them.
 *
 * 🚨 ONE LIST, TWO SCREENS. The account card offers these as "Benefit Priority"; the payer-overrides
 * dialog offers the same five as a SCOPE ("this override applies only where the account is set to
 * …"), which is the one place the legacy category is legitimately a scope rather than a policy. Two
 * copies of these labels would drift, and a scope spelled differently from the setting it names is a
 * scope nobody can check against the account.
 *
 * The account card prepends its own "Not set" option, which is a policy choice and not a category —
 * an override scoped to "not set" would be an override scoped to nothing.
 */
export const SERVICE_CATEGORY_OPTIONS: Array<{ name: string; value: string }> =
  [
    { name: "ASC Facility first (surgery centers)", value: "SurgeryCenter" },
    { name: "Hospital Outpatient first (hospitals)", value: "Hospital" },
    {
      name: "Specialist Office first (clinics, in-office specialists)",
      value: "Clinic",
    },
    {
      name: "By payer — Specialist Office for Medicare Advantage, Surgical for the rest",
      value: "OfficeOrSurgicalByPayerType",
    },
    { name: "Lincoln custom rules (legacy)", value: "Lincoln" },
  ];

export const serviceCategoryLabel = (
  value: string | null | undefined
): string =>
  SERVICE_CATEGORY_OPTIONS.find((option) => option.value === value)?.name ??
  value ??
  "";

/** The three whose line selection a person can edit. Deductible and OOP read plan level. */
export const CONFIGURABLE_BENEFITS = [
  "FLAT_COPAY",
  "COINSURANCE_PERCENT",
  "PROFESSIONAL_COPAY",
] as const;

/**
 * The tie-break, in the benefit's OWN unit. A co-insurance is a percent and a copay is an amount;
 * one shared "Highest" made the control read as if the two were the same quantity.
 */
export const tieBreakOptions = (
  benefit: string
): Array<{ label: string; value: string }> => {
  const unit = benefit === "COINSURANCE_PERCENT" ? "percent" : "amount";
  return [
    { label: `Highest ${unit}`, value: "HIGHEST" },
    { label: `Lowest ${unit}`, value: "LOWEST" },
    { label: "First in the response", value: "FIRST" },
  ];
};

/**
 * The two tiers a service line can be read at — `AccountBenefitMap.tierPreference`.
 *
 * 🚨 A TIER IS THIS FACILITY'S STANDING IN A PAYER'S NETWORK, NOT A PROPERTY OF THE PLAN. Measured
 * over 90 days of stored responses, United Healthcare is the only payer that sends per-tier
 * amounts: `P - TIER 1` (sometimes naming a health system, `P - TIER 1 MERCY`) is the
 * designated/preferred provider with the lowest cost share, `2 - TIER 2` is an ordinary in-network
 * provider, and Tier 3 / Tier 4 are out of network and never read. So the words are the payer's
 * standing rather than the engine's step number.
 *
 * 🚨 THE SETTING ONLY DOES ANYTHING WHERE THE PAYER TIERED THE PLAN. On a response that describes
 * no tier it changes nothing. `Tier2` is the seeded value and what every account reads today, which
 * is why "Standard" is the one that reads as unremarkable.
 *
 * Two entries and no "not set": a service line is always read at one tier or the other.
 */
export const TIER_CHOICES: Array<{
  label: string;
  value: BenefitTierPreference;
}> = [
  { label: "Preferred (Tier 1)", value: "Tier1" },
  { label: "Standard (Tier 2)", value: "Tier2" },
];

export const tierLabel = (value: BenefitTierPreference): string =>
  TIER_CHOICES.find((tier) => tier.value === value)?.label ?? value;

/**
 * The payer classes an exception can be scoped to — `PayerType` on the API.
 *
 * Kept as a literal list rather than derived from a payer dropdown: an exception scoped to a class
 * applies to payers this account has not seen yet, which is the whole point of scoping by class.
 *
 * Reuses the payer-rules page's own list so the four `PayerType` values are spelled the same way in
 * both places — a second list would drift and a class the API rejects would look like a save bug.
 */
export const PAYER_CLASS_OPTIONS = payerTypes;

/** pVerify's own place-of-service labels, for the "list" branch of a place-of-service selector. */
export const PLACE_OF_SERVICE_OPTIONS = [
  "Outpatient Hospital",
  "Ambulatory Surgical Center",
  "Inpatient Hospital",
  "Office",
];

export const PERIOD_OPTIONS = ["Visit", "Admisson", "Calendar Year", "Episode"];

export const COVERAGE_LEVEL_OPTIONS = [
  "Individual",
  "Family",
  "Employee and Spouse",
  "Employee and Children",
];
